import React, { useState, useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { logAnalyticsEvent } from "./firebase";
import Header from "./components/Header";
import OptimizedHomeVideo from "./components/OptimizedHomeVideo";
import LoadingSpinner from "./components/LoadingSpinner";
import PageTransition from "./components/PageTransition";
import { initLazyLoading, isMobileDevice } from "./utils/lazyLoader";
import "./styles/main.scss";
// Videos moved to public/ to avoid bundling (saves 4-10MB)
const video = "/assets/videos/video.webm";
const mobilevideo = "/assets/videos/mobile-video.webm";

// Lazy load components for code splitting (including Footer/Copyright for better initial load)
const Footer = lazy(() => import("./components/Footer"));
const Copyright = lazy(() => import("./components/Copyright"));
const About = lazy(() => import("./components/About"));
const Services = lazy(() => import("./components/Services"));
const Contact = lazy(() => import("./components/Contact"));
const Products = lazy(() => import("./Products"));
const DalleKhursani = lazy(() => import("./components/DalleKhursani"));
const GhostPepper = lazy(() => import("./components/GhostPepper"));
const HardCheese = lazy(() => import("./components/HardCheese"));
const BeetrootPowder = lazy(() => import("./components/products/BeetrootPowder"));
const DehydratedGarlic = lazy(() => import("./components/products/DehydratedGarlic"));
const LargeCardamom = lazy(() => import("./components/products/LargeCardamom"));
const HoneyApis = lazy(() => import("./components/products/HoneyApis"));
const Blogs = lazy(() => import("./components/Blogs"));
const Blog = lazy(() => import("./components/Blog"));

function App() {
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  useEffect(() => {
    // Initialize lazy loading for non-critical resources
    initLazyLoading();
    
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <MainLayout isMobile={isMobile} setIsMobile={setIsMobile} />
      </Router>
    </HelmetProvider>
  );
}

// Loading component for Suspense fallback
const Loading = () => <LoadingSpinner />;

function MainLayout({ isMobile }) {
  const location = useLocation();
  const isBlogRoute = location.pathname.startsWith('/blogs');
  function ScrollToTop() {
    useEffect(() => {
      // Track page views
      logAnalyticsEvent('page_view', {
        page_title: document.title,
        page_location: window.location.href,
        page_path: location.pathname
      });

      if (window.location.hash === "#contact") {
        setTimeout(() => {
          const contactElement = document.getElementById("contact");
          if (contactElement) {
            contactElement.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        window.scrollTo(0, 0);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
    return null;
  }

  return (
    <>
      <PageTransition />
      <Helmet>
        <title>Leading Exporter of Himalayan Organic Products | MEVEDIR</title>
        <meta name="description" content="MEVEDIR is India's leading exporter of Dalle Khursani, Ghost Pepper, hard cheese, and Himalayan organics. Premium quality products from Sikkim with organic certification where applicable." />
        <meta name="keywords" content="Himalayan organic products, Indian exporters, Sikkim organic, Dalle Khursani exporter, Ghost Pepper exporter, Churpi exporter, premium organic exports" />
        <link rel="canonical" href="https://mevedir.com/" />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Leading Exporter of Himalayan Organic Products | MEVEDIR" />
        <meta property="og:description" content="MEVEDIR is India's leading exporter of Dalle Khursani, Ghost Pepper, hard cheese, and Himalayan organics. Premium quality products from Sikkim with organic certification where applicable." />
        <meta property="og:url" content="https://mevedir.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="MEVEDIR" />
        <meta property="og:image" content="https://mevedir.com/mevedir-logo.png" />
        <meta property="og:image:alt" content="MEVEDIR - Leading Exporter of Himalayan Organic Products" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Leading Exporter of Himalayan Organic Products | MEVEDIR" />
        <meta name="twitter:description" content="MEVEDIR is India's leading exporter of Dalle Khursani, Ghost Pepper, hard cheese, and Himalayan organics. Premium quality products from Sikkim with organic certification where applicable." />
        <meta name="twitter:image" content="https://mevedir.com/mevedir-logo.png" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="MEVEDIR" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="IN-SK" />
        <meta name="geo.placename" content="Sikkim, India" />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify((() => {
            const org = {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "MEVEDIR",
              "description": "Leading exporter of Himalayan organic products from Sikkim, India",
              "url": "https://mevedir.com",
              "logo": "https://mevedir.com/mevedir-logo.png",
              "address": {
                "@type": "PostalAddress",
                "addressRegion": "Sikkim",
                "addressCountry": "India"
              },
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "saileshsherpa@mevedir.com",
                "contactType": "customer service"
              },
              "sameAs": [
                "https://himalayfrsh.com",
                "https://www.vartohorganic.com"
              ]
            };
            if (!isBlogRoute) {
              org.hasOfferCatalog = {
                "@type": "OfferCatalog",
                "name": "Himalayan Organic Products",
                "itemListElement": [
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Product",
                      "name": "Dalle Khursani",
                      "description": "Premium GI-tagged cherry pepper from Sikkim"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Product",
                      "name": "Ghost Pepper",
                      "description": "Bhut Jolokia - one of the world's hottest chilies"
                    }
                  },
                  {
                    "@type": "Offer",
                    "itemOffered": {
                      "@type": "Product",
                      "name": "Churpi/Chhurpi",
                      "description": "Traditional Himalayan hard cheese"
                    }
                  }
                ]
              };
            }
            return org;
          })())}
        </script>
      </Helmet>
      <ScrollToTop />
      {/* Hide Header on /products and /blogs pages */}
      {location.pathname !== "/products" && <Header />}

      <Routes>
        <Route
          path="/"
          element={
            <div id="home-container">
              <Home isMobile={isMobile} />
              <Suspense fallback={<Loading />}>
                <About />
                <Services />
                <Contact />
                <Footer />
                <Copyright />
              </Suspense>
              <a
                href="https://wa.me/919883965595"
                className="home-whatsapp-float"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Chat on WhatsApp"
              >
                <i className="fa-brands fa-whatsapp"></i>
              </a>
            </div>
          }
        />
        <Route path="/products" element={
          <>
            <Suspense fallback={<Loading />}>
              <Products />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route path="/blogs" element={
          <>
            <Suspense fallback={<Loading />}>
              <Blogs />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route path="/blogs/:slug" element={
          <>
            <Suspense fallback={<Loading />}>
              <Blog />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route
          path="/dalle-khursani"
          element={
            <>
              <Suspense fallback={<Loading />}>
                <DalleKhursani />
                <Footer />
                <Copyright />
              </Suspense>
            </>
          }
        />
        <Route
          path="/ghost-pepper"
          element={
            <>
              <Suspense fallback={<Loading />}>
                <GhostPepper />
                <Footer />
                <Copyright />
              </Suspense>
            </>
          }
        />
        <Route
          path="/hard-cheese"
          element={
            <>
              <Suspense fallback={<Loading />}>
                <HardCheese />
                <Footer />
                <Copyright />
              </Suspense>
            </>
          }
        />
        <Route path="/beetroot-powder" element={
          <>
            <Suspense fallback={<Loading />}>
              <BeetrootPowder />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route path="/dehydrated-garlic" element={
          <>
            <Suspense fallback={<Loading />}>
              <DehydratedGarlic />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route path="/large-cardamom" element={
          <>
            <Suspense fallback={<Loading />}>
              <LargeCardamom />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
        <Route path="/honey-apis" element={
          <>
            <Suspense fallback={<Loading />}>
              <HoneyApis />
              <Footer />
              <Copyright />
            </Suspense>
          </>
        } />
      </Routes>
    </>
  );
}

function Home({ isMobile }) {
  return (
    <main className="main-content">
      <section className="section1" id="section1">
        {/* SEO H1 Heading - Hidden visually but accessible to screen readers */}
        <h1 style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          Leading Exporter of Himalayan Organic Products - MEVEDIR
        </h1>
        
        {/* Optimized video loading with poster image for better mobile performance */}
        <OptimizedHomeVideo 
          isMobile={isMobile}
          videoSrc={video}
          mobileSrc={mobilevideo}
        />
      </section>
    </main>
  );
}

export default App;
